import { APP_DETAILS_TAB } from '@constants';
import { InAppNotification, InAppNotificationType } from '@interfaces';
import { getCandidateVerificationRoute } from './notifications.registry.helpers';
import { NotificationRegistry } from './notifications.registry.types';

type StaffNotificationType =
  | InAppNotificationType.FOREIGNER_SENT_PRE_SURVEY_FOR_VERIFICATION
  | InAppNotificationType.FOREIGNER_SENT_APPLICATION_TO_VERIFICATION
  | InAppNotificationType.MANAGER_STARTED_NEW_CANDIDATE_VERIFICATION
  | InAppNotificationType.CANDIDATE_FILLED_OPEN_FORM
  | InAppNotificationType.CANDIDATE_FILLED_SUPPLEMENTARY_FORM
  | InAppNotificationType.CANDIDATE_VERIFICATION_SUPPLEMENTARY_INFO_REQUESTED
  | InAppNotificationType.CANDIDATE_REQUESTED_EXTENDING_TIME_FOR_FILLING_FORM
  | InAppNotificationType.MANAGER_CHANGED_CANDIDATE_VERIFICATION_PROCEDURES_LIST
  | InAppNotificationType.MANAGER_ISSUED_CANDIDATE_VERIFICATION_DECISION
  | InAppNotificationType.FOREIGNER_ADDED_DOCUMENT_COMMENT
  | InAppNotificationType.FOREIGNER_SET_AVAILABILITY
  | InAppNotificationType.DOCUMENTS_GENERATION_SUCCESS
  | InAppNotificationType.DOCUMENTS_SENT_TO_EXTERNAL_SYSTEM_AND_GENERATED
  | InAppNotificationType.DOCUMENTS_GENERATION_ERROR
  | InAppNotificationType.CHECKLIST_GENERATION_SUCCESS
  | InAppNotificationType.CHECKLIST_GENERATION_ERROR
  | InAppNotificationType.FOREIGNER_HAS_BEEN_ASSIGNED_TO_YOU
  | InAppNotificationType.FOREIGNER_SENT_MESSAGE_IN_CHAT
  | InAppNotificationType.FOREIGNER_CHANGED_PERSONAL_DETAILS
  | InAppNotificationType.FOREIGNER_SENDS_APPLICATION_WITH_POST
  | InAppNotificationType.FOREIGNER_APPLICATION_MODE_HAS_CHANGED
  | InAppNotificationType.FOREIGNER_PICKED_SIGNING_MODE
  | InAppNotificationType.FOREIGNER_CHANGED_PLANNED_DOCUMENTS_SIGNING_MODE
  | InAppNotificationType.FOREIGNER_CHANGED_PLANNED_DOCUMENTS_SIGNING_DATE
  | InAppNotificationType.FOREIGNER_SET_IMMIGRATION_OFFICE_VISIT_DATE
  | InAppNotificationType.FOREIGNER_CHANGED_IMMIGRATION_OFFICE_VISIT_DATE
  | InAppNotificationType.FOREIGNER_PLANNING_TO_COME_TO_THE_OFFICE
  | InAppNotificationType.FOREIGNER_CANCELLED_VISIT_IN_COMPANY_OFFICE
  | InAppNotificationType.FOREIGNER_ADDED_ITEMS_TO_REVIEW
  | InAppNotificationType.FOREIGNER_MADE_PURCHASE
  | InAppNotificationType.REASSIGNED_FOREIGNERS_TO_YOU
  | InAppNotificationType.PARTNER_REQUESTED_PAYOUT
  | InAppNotificationType.FOREIGNER_REQUESTED_APPOINTMENT_DATE
  | InAppNotificationType.FOREIGNER_CHANGED_REQUESTED_APPOINTMENT
  | InAppNotificationType.FOREIGNER_CANCELLED_APPOINTMENT
  | InAppNotificationType.FOREIGNER_UPLOADED_DECISION
  | InAppNotificationType.FOREIGNER_UPLOADED_WORK_PERMIT_DOCUMENTS;

const getManagementApplicationDetailsRoute = (
  notification: InAppNotification,
  appDetailsTab: APP_DETAILS_TAB,
  queryParams: Record<string, unknown> = {}
) => ({
  linkParams: ['management', 'application-details', notification.relatedForeignerId, notification.relatedUserProcessId],
  extras: {
    queryParams: {
      appDetailsTab,
      ...queryParams,
    },
  },
});

const getVisibleDateRoute = (rawDate?: string | null) => ({
  linkParams: ['management', 'visits-calendar-new'],
  extras: {
    queryParams: { visibleDate: rawDate || '' },
  },
});

export const staffNotificationRegistry: NotificationRegistry<StaffNotificationType> = {
  [InAppNotificationType.FOREIGNER_SENT_PRE_SURVEY_FOR_VERIFICATION]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-pre-survey-refresh',
    icons: [{ name: 'pending_actions', className: 'foreigner-sent-application-to-verification-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.PRE_SURVEY),
  },
  [InAppNotificationType.FOREIGNER_SENT_APPLICATION_TO_VERIFICATION]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-general-refresh',
    icons: [{ name: 'pending_actions', className: 'foreigner-sent-application-to-verification-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.GENERAL),
  },
  [InAppNotificationType.MANAGER_STARTED_NEW_CANDIDATE_VERIFICATION]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'assignment', className: 'primary-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'basic-details'),
  },
  [InAppNotificationType.CANDIDATE_FILLED_OPEN_FORM]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'fact_check', className: 'positive-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'form-verification'),
  },
  [InAppNotificationType.CANDIDATE_FILLED_SUPPLEMENTARY_FORM]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'fact_check', className: 'positive-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'form-verification'),
  },
  [InAppNotificationType.CANDIDATE_VERIFICATION_SUPPLEMENTARY_INFO_REQUESTED]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'assignment_late', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'form-verification'),
  },
  [InAppNotificationType.CANDIDATE_REQUESTED_EXTENDING_TIME_FOR_FILLING_FORM]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'schedule', className: 'objective-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'form-verification'),
  },
  [InAppNotificationType.MANAGER_CHANGED_CANDIDATE_VERIFICATION_PROCEDURES_LIST]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'list_alt', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'result'),
  },
  [InAppNotificationType.MANAGER_ISSUED_CANDIDATE_VERIFICATION_DECISION]: {
    recipientGroup: 'staff',
    actionKey: 'candidate-verification',
    autoReadOn: ['cvm-details'],
    icons: [{ name: 'gavel', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: (notification, myself) => getCandidateVerificationRoute(notification, myself, 'result'),
  },
  [InAppNotificationType.FOREIGNER_ADDED_DOCUMENT_COMMENT]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-docs-refresh',
    icons: [{ name: 'mms', className: 'foreigner-sent-message-in-chat-icon' }],
    getRoute: notification =>
      getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.DOCS, {
        revealUserDocumentId: notification.relatedUserDocumentId,
        openChatForUserDocumentId: notification.relatedUserDocumentId,
      }),
  },
  [InAppNotificationType.FOREIGNER_SET_AVAILABILITY]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'calendar_today', className: 'foreigner-set-availability-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.DOCUMENTS_GENERATION_SUCCESS]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'print', className: 'documents-generation-success-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.DOCUMENTS_SENT_TO_EXTERNAL_SYSTEM_AND_GENERATED]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [
      { name: 'domain_verification', className: 'primary-success-icon', fontSet: 'material-icons-outlined' },
      { name: 'print', className: 'documents-generation-success-icon' },
    ],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.DOCUMENTS_GENERATION_ERROR]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'print_disabled', className: 'documents-generation-error-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.CHECKLIST_GENERATION_SUCCESS]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'checklist', className: 'documents-generation-success-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.CHECKLIST_GENERATION_ERROR]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'playlist_remove', className: 'documents-generation-error-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },

  [InAppNotificationType.FOREIGNER_HAS_BEEN_ASSIGNED_TO_YOU]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    icons: [{ name: 'person', className: 'foreigner-assigned-icon' }],
    getRoute: notification => ({
      linkParams: ['management', 'foreigner-details', notification.relatedForeignerId],
      extras: { queryParams: { tabIndex: 0 } },
    }),
  },
  [InAppNotificationType.FOREIGNER_SENT_MESSAGE_IN_CHAT]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    icons: [{ name: 'forum', className: 'new-chat-message-icon' }],
    getRoute: notification => ({
      linkParams: [
        'management',
        'application-details',
        notification.relatedForeignerId,
        notification.relatedUserProcessId,
      ],
    }),
  },
  [InAppNotificationType.FOREIGNER_CHANGED_PERSONAL_DETAILS]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-general-path-refresh',
    icons: [{ name: 'warning', className: 'changed-personal-details-icon' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.GENERAL),
  },
  [InAppNotificationType.FOREIGNER_SENDS_APPLICATION_WITH_POST]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'mark_as_unread', className: 'foreigner-sends-application-with-post' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.FOREIGNER_APPLICATION_MODE_HAS_CHANGED]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'priority_high', className: 'objective-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.FOREIGNER_PICKED_SIGNING_MODE]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    autoReadOn: ['summary'],
    icons: [{ name: 'draw', className: 'new-chat-message-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.FOREIGNER_CHANGED_PLANNED_DOCUMENTS_SIGNING_MODE]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    autoReadOn: ['summary'],
    icons: [{ name: 'draw', className: 'changed-personal-details-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.FOREIGNER_CHANGED_PLANNED_DOCUMENTS_SIGNING_DATE]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    icons: [{ name: 'calendar_month', className: 'changed-personal-details-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY),
  },
  [InAppNotificationType.FOREIGNER_SET_IMMIGRATION_OFFICE_VISIT_DATE]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    icons: [{ name: 'calendar_month', className: 'primary-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getVisibleDateRoute(notification.data?.additionalInfo?.visitDate || ''),
  },
  [InAppNotificationType.FOREIGNER_CHANGED_IMMIGRATION_OFFICE_VISIT_DATE]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    icons: [{ name: 'calendar_month', className: 'objective-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getVisibleDateRoute(notification.data?.additionalInfo?.visitDate || ''),
  },
  [InAppNotificationType.FOREIGNER_PLANNING_TO_COME_TO_THE_OFFICE]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    autoReadOn: ['summary'],
    icons: [{ name: 'calendar_month', className: 'primary-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getVisibleDateRoute(notification.data?.additionalInfo?.newDate || ''),
  },

  [InAppNotificationType.FOREIGNER_CANCELLED_VISIT_IN_COMPANY_OFFICE]: {
    recipientGroup: 'staff',
    actionKey: 'noop',
    autoReadOn: ['summary'],
    icons: [{ name: 'cancel', className: 'negative-icon', fontSet: 'material-icons-outlined' }],
    getRoute: () => null,
  },

  [InAppNotificationType.FOREIGNER_ADDED_ITEMS_TO_REVIEW]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-review-items-refresh',
    icons: [{ name: 'attach_file_add', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification =>
      getManagementApplicationDetailsRoute(notification, APP_DETAILS_TAB.SUMMARY, { showReviewItems: true }),
  },

  [InAppNotificationType.FOREIGNER_MADE_PURCHASE]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    icons: [{ name: 'payments', className: 'foreigner-made-purchase-icon material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: ['management', 'foreigner-details', notification.relatedForeignerId],
      extras: { queryParams: { tabIndex: 1 } },
    }),
  },
  [InAppNotificationType.REASSIGNED_FOREIGNERS_TO_YOU]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    icons: [{ name: 'groups', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: ['management', 'applications-list'],
      extras: { queryParams: { creatorId: notification.recipientId } },
    }),
  },
  [InAppNotificationType.PARTNER_REQUESTED_PAYOUT]: {
    recipientGroup: 'staff',
    actionKey: 'partner-payouts',
    icons: [{ name: 'attach_money', className: 'action-icon', fontSet: 'material-symbols-outlined' }],
    getRoute: () => ({ linkParams: ['partners', 'payouts'] }),
  },
  [InAppNotificationType.FOREIGNER_REQUESTED_APPOINTMENT_DATE]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    autoReadOn: ['summary'],
    icons: [{ name: 'event_available', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: [
        'management',
        'application-details',
        notification.relatedForeignerId,
        notification.relatedUserProcessId,
      ],
      extras: { queryParams: { appDetailsTab: 'SUMMARY' } },
    }),
  },
  [InAppNotificationType.FOREIGNER_CHANGED_REQUESTED_APPOINTMENT]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    autoReadOn: ['summary'],
    icons: [{ name: 'event_available', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: [
        'management',
        'application-details',
        notification.relatedForeignerId,
        notification.relatedUserProcessId,
      ],
      extras: { queryParams: { appDetailsTab: 'SUMMARY' } },
    }),
  },
  [InAppNotificationType.FOREIGNER_CANCELLED_APPOINTMENT]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    autoReadOn: ['summary'],
    icons: [{ name: 'event_busy_outline', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: [
        'management',
        'application-details',
        notification.relatedForeignerId,
        notification.relatedUserProcessId,
      ],
      extras: { queryParams: { appDetailsTab: 'SUMMARY' } },
    }),
  },
  [InAppNotificationType.FOREIGNER_UPLOADED_DECISION]: {
    recipientGroup: 'staff',
    actionKey: 'management-application-summary-refresh',
    autoReadOn: ['summary'],
    icons: [{ name: 'event_available', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: [
        'management',
        'application-details',
        notification.relatedForeignerId,
        notification.relatedUserProcessId,
      ],
      extras: { queryParams: { appDetailsTab: 'SUMMARY' } },
    }),
  },
  [InAppNotificationType.FOREIGNER_UPLOADED_WORK_PERMIT_DOCUMENTS]: {
    recipientGroup: 'staff',
    actionKey: 'route-only',
    autoReadOn: ['work-permit-modal'],
    icons: [{ name: 'upload_file', className: 'foreigner-sent-application-to-verification-icon' }],
    getRoute: notification => ({
      linkParams: ['work-permits'],
      extras: { queryParams: { openWorkPermitId: notification.relatedWorkPermitId } },
    }),
  },
};
